import React, { useState } from 'react'
import { X, Upload, AlertCircle, CheckCircle, Send, Camera } from 'lucide-react'
import { cn } from '../../utils/cn'

const SupportForm = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'medium',
    screenshot: null
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-blue-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ]

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleFileSelect = (file) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (PNG, JPG, GIF, WEBP)')
        return
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB')
        return
      }

      setFormData(prev => ({ ...prev, screenshot: file }))
      setError('')
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    handleFileSelect(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    handleFileSelect(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const removeScreenshot = () => {
    setFormData(prev => ({ ...prev, screenshot: null }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.subject.trim()) {
      setError('Subject is required')
      return
    }

    if (!formData.message.trim()) {
      setError('Message is required')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const submitData = new FormData()
      submitData.append('subject', formData.subject.trim())
      submitData.append('message', formData.message.trim())
      submitData.append('priority', formData.priority)
      
      if (formData.screenshot) {
        submitData.append('screenshot', formData.screenshot)
      }

      await onSubmit(submitData)
      
      setSuccess(true)
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          subject: '',
          message: '',
          priority: 'medium',
          screenshot: null
        })
        setSuccess(false)
        onClose()
      }, 2000)
      
    } catch (err) {
      setError(err.message || 'Failed to submit support request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const takeScreenshot = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { mediaSource: 'screen' }
        })
        
        const video = document.createElement('video')
        video.srcObject = stream
        video.play()
        
        video.addEventListener('loadedmetadata', () => {
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0)
          
          // Stop the screen capture stream
          stream.getTracks().forEach(track => track.stop())
          
          // Convert canvas to blob
          canvas.toBlob((blob) => {
            const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' })
            handleFileSelect(file)
          }, 'image/png')
        })
      } else {
        setError('Screen capture is not supported in your browser')
      }
    } catch (err) {
      console.error('Screenshot error:', err)
      setError('Failed to capture screenshot. Please try uploading an image instead.')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-all"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-2xl mx-auto bg-white dark:bg-[#1a1625] rounded-lg shadow-xl border-0">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-[#2f2b41]">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-accent-100 dark:bg-accent-900/30 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-accent-600 dark:text-accent-400" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                Need Help?
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-neutral-100 dark:hover:bg-[#2f2b41] rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Success State */}
          {success && (
            <div className="p-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
                Support Request Sent!
              </h4>
              <p className="text-neutral-600 dark:text-neutral-400">
                We've received your request and will get back to you soon.
              </p>
            </div>
          )}

          {/* Form */}
          {!success && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Brief description of your issue"
                  maxLength={200}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400"
                  required
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {formData.subject.length}/200 characters
                </p>
              </div>

              {/* Priority */}
              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Please describe your issue in detail. Include steps to reproduce if applicable."
                  rows={6}
                  maxLength={5000}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 resize-none"
                  required
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {formData.message.length}/5000 characters
                </p>
              </div>

              {/* Screenshot Upload */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Screenshot (Optional)
                </label>
                
                {!formData.screenshot ? (
                  <div className="space-y-3">
                    {/* Drop Zone */}
                    <div
                      className={cn(
                        'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                        dragOver
                          ? 'border-accent-400 bg-accent-50 dark:bg-accent-900/20'
                          : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500'
                      )}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                      <p className="text-neutral-600 dark:text-neutral-400 mb-2">
                        Drag and drop an image here, or
                      </p>
                      <div className="flex items-center justify-center space-x-3">
                        <label className="cursor-pointer">
                          <span className="inline-flex items-center px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-[#221f2f] hover:bg-neutral-50 dark:hover:bg-[#2f2b41] transition-colors">
                            <Upload className="w-4 h-4 mr-2" />
                            Browse Files
                          </span>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                        </label>
                        <span className="text-neutral-500">or</span>
                        <button
                          type="button"
                          onClick={takeScreenshot}
                          className="inline-flex items-center px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-[#221f2f] hover:bg-neutral-50 dark:hover:bg-[#2f2b41] transition-colors"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Take Screenshot
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                        PNG, JPG, GIF, WEBP up to 5MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="border border-neutral-300 dark:border-[#2f2b41] rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                            {formData.screenshot.name}
                          </p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            {(formData.screenshot.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeScreenshot}
                        className="p-2 text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-neutral-100 dark:hover:bg-[#2f2b41] rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-neutral-200 dark:border-[#2f2b41]">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-[#221f2f] border border-neutral-300 dark:border-[#2f2b41] rounded-lg hover:bg-neutral-50 dark:hover:bg-[#2f2b41] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.subject.trim() || !formData.message.trim()}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-accent-600 border border-transparent rounded-lg hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Support Request
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default SupportForm